import React from "react";
import { Box, Typography, Button, Theme } from "@mui/material";
import { Copy } from "lucide-react";
import TextField from "../common/TextField";
import ViewModeToggle from "./ViewModeToggle";
import { useSnackpack } from "../context/SnackpackContextProvider";
import FlexBox from "components/common/FlexBox";
import useIsMobile from "components/hooks/useIsMobile";
import LinkRouter from "components/navigation/LinkRouter";
import DateDisplay from "components/common/DateDisplay";
import { AccountStub } from "protogen/common_pb";

interface FormHeaderProps {
  title: string;
  description: string;
  uuid: string;
  editable?: boolean;
  onTitleChange?: (title: string) => void;
  onDescriptionChange?: (description: string) => void;
  showViewModeToggle?: boolean;
  viewMode?: "edit" | "view" | "results";
  setViewMode?: (mode: "edit" | "view" | "results") => void;
  additionalOptions?: React.ReactNode;
  showCopyLink?: boolean;
  submitter?: AccountStub;
  submitterDate?: Date;
}

const FormHeader = ({
  title,
  description,
  uuid,
  editable = false,
  onTitleChange,
  onDescriptionChange,
  showViewModeToggle = false,
  viewMode,
  setViewMode,
  additionalOptions,
  showCopyLink = true,
  submitter,
  submitterDate,
}: FormHeaderProps) => {
  const isMobile = useIsMobile();
  const { pushSnack } = useSnackpack();

  const handleCopyLink = () => {
    navigator.clipboard.writeText(
      `${window.location.origin}/forms/complete/${uuid}`,
    );
    pushSnack("Link copied to clipboard");
  };

  return (
    <FlexBox direction="column" gap="16px">
      <FlexBox
        direction={isMobile ? "column-reverse" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "center"}
        gap="12px"
        sx={{ minHeight: "55px" }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0, flexShrink: 1 }}>
          {editable ? (
            <TextField
              variant="standard"
              value={title}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onTitleChange?.(e.target.value)
              }
              margin="none"
              placeholder="Untitled Form"
              multiline
              InputProps={{
                disableUnderline: true,
                sx: (theme: Theme) => ({
                  ...theme.typography.title,
                  fontSize: "28px",
                  fontWeight: "700",
                  fontFamily: "AlbertSans",
                  borderRadius: "8px",
                  padding: isMobile ? "0" : "11px 0 10px 0",
                  letterSpacing: "-0.4px",
                }),
              }}
            />
          ) : (
            <Typography
              variant="h1"
              sx={{
                wordBreak: "break-word",
                padding: isMobile ? "0" : "11px 0 10px 0",
              }}
            >
              {title}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: isMobile ? "start" : "center",
            gap: "8px",
            flexDirection: isMobile ? "column" : "row",
            flexShrink: 0,
            minWidth: "fit-content",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexShrink: 0,
            }}
          >
            {showCopyLink && (
              <Button
                sx={{ flexShrink: 0 }}
                variant="text"
                endIcon={<Copy size={18} />}
                onClick={handleCopyLink}
              >
                Copy responder link
              </Button>
            )}
            {additionalOptions}
          </Box>
          {showViewModeToggle && viewMode && setViewMode && (
            <Box sx={{ flexShrink: 0 }}>
              <ViewModeToggle
                viewMode={viewMode}
                setViewMode={setViewMode}
                sx={{ flexShrink: 0, width: isMobile ? "100%" : "auto" }}
              />
            </Box>
          )}
        </Box>
      </FlexBox>

      {editable ? (
        <TextField
          variant="detachedOutlined"
          placeholder="Description"
          value={description}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onDescriptionChange?.(e.target.value)
          }
          fullWidth
          multiline
          minRows={2}
          InputProps={{
            sx: { pb: "20px" },
          }}
        />
      ) : (
        description &&
        !(viewMode === "results") && (
          <Typography variant="body">{description}</Typography>
        )
      )}
      {submitter && submitterDate && (
        <Typography
          variant="bodyHeavy"
          display="flex"
          gap="4px"
          flexDirection="row"
          alignItems="center"
        >
          Submitted by{" "}
          <LinkRouter
            to={submitter.isAdvisor ? `${submitter.profilePath}` : undefined}
            inline
          >
            <span
              style={{
                color: "#198282",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              {submitter.displayName}
            </span>
          </LinkRouter>
          {" • "}
          <DateDisplay date={submitterDate} />
        </Typography>
      )}
    </FlexBox>
  );
};

export default FormHeader;
